import React, {useState, useEffect, useMemo} from 'react';
import {TouchableOpacity, View, TextInput, Text} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import Ionicons from 'react-native-vector-icons/Ionicons';
import {Chess, validateFen} from 'chess.js';
import {Chessboard} from 'react-chessboard';
// Relative, not '@/': this module is also loaded by jest suites that do not
// map the alias for every transitive import.
import useChessboardColorTheme from '../../Hooks/ChessboardColorTheme/useChessboardColorTheme';

export const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

/**
 * Set up a position by hand: pick a piece from the palette and click a
 * square, drag pieces around or off the board, erase, choose the side to
 * move and castling rights, or type a FEN. Applies only a legal position.
 *
 * Extracted unchanged from Create New Study (where it is "Popup 2", opened
 * from the Editor position card) so the course editor can fix a misdetected
 * puzzle position with the same tool. Web-only: it uses DOM drag and drop
 * for the palette.
 */
export default function BoardEditor({
  initialFen,
  onApply,
  onClose,
  minFontSize = 0,
}: {
  initialFen: string;
  onApply: (fen: string) => void;
  onClose: () => void;
  /**
   * Floor for every font size. 0 (the default) keeps the compact type Create
   * New Study was designed with; the course editor passes 12, the floor its
   * screens keep.
   */
  minFontSize?: number;
}) {
  const fs = (size: number) => Math.max(size, minFontSize);
  const chessboardColorTheme = useChessboardColorTheme();

  const squareColors = useMemo(() => {
    if (chessboardColorTheme === 'green-white') {
      return {light: '#EBECD0', dark: '#739552'};
    } else if (chessboardColorTheme === 'blue-white') {
      return {light: '#DFDFDF', dark: '#6F84B4'};
    } else {
      return {light: '#edd899', dark: '#d1ab41'};
    }
  }, [chessboardColorTheme]);

  const [editorChess] = useState(() => {
    const c = new Chess();
    if (initialFen) {
      try {
        c.load(initialFen);
      } catch (e) {}
    }
    return c;
  });
  const [fen, setFen] = useState(editorChess.fen());
  const [editorError, setEditorError] = useState('');
  const [selectedPiece, setSelectedPiece] = useState<{type: string; color: string} | null>(null);
  const [eraserActive, setEraserActive] = useState(false);
  const [turn, setTurn] = useState<'w' | 'b'>('w');

  const [wk, setWk] = useState(true);
  const [wq, setWq] = useState(true);
  const [bk, setBk] = useState(true);
  const [bq, setBq] = useState(true);

  useEffect(() => {
    if (initialFen) {
      const parts = initialFen.split(' ');
      if (parts.length > 1) {
        setTurn(parts[1] as any);
      }
      if (parts.length > 2) {
        const cRights = parts[2];
        setWk(cRights.includes('K'));
        setWq(cRights.includes('Q'));
        setBk(cRights.includes('k'));
        setBq(cRights.includes('q'));
      }
    }
  }, [initialFen]);

  const updateFenState = () => {
    const position = editorChess.fen().split(' ')[0];
    // The wk/wq/bk/bq checkboxes only record the coach's *intent* - they
    // don't get cleared when a king or rook is dragged/erased off its home
    // square, so a right could end up claimed here for a rook that's no
    // longer on the board. chess.js's own validateFen doesn't catch that,
    // but a stricter validator (python-chess, used server-side for
    // practice_start_fen) rejects it as an illegal position. Only emit a
    // right when the piece it depends on is actually still in place, so the
    // editor can never produce that.
    const hasPieceAt = (square: string, type: string, color: 'w' | 'b') => {
      const piece = editorChess.get(square as any);
      return !!piece && piece.type === type && piece.color === color;
    };
    const whiteKingHome = hasPieceAt('e1', 'k', 'w');
    const blackKingHome = hasPieceAt('e8', 'k', 'b');
    let castling = '';
    if (wk && whiteKingHome && hasPieceAt('h1', 'r', 'w')) castling += 'K';
    if (wq && whiteKingHome && hasPieceAt('a1', 'r', 'w')) castling += 'Q';
    if (bk && blackKingHome && hasPieceAt('h8', 'r', 'b')) castling += 'k';
    if (bq && blackKingHome && hasPieceAt('a8', 'r', 'b')) castling += 'q';
    if (!castling) castling = '-';

    const newFen = `${position} ${turn} ${castling} - 0 1`;
    // Board interactions (click-to-place, drag-drop, erase, clear) mutate
    // editorChess's board directly via put()/remove(), which never validate
    // - a removed king is really gone from _board. So run the same
    // validateFen check the manual FEN textbox uses below, instead of just
    // try/catching load() and swallowing the error: load() validates before
    // touching _board, so a caught failure here would otherwise silently
    // drop the error and leave "Apply Position" enabled on an illegal king
    // count.
    const check = validateFen(newFen);
    if (!check.ok) {
      setFen(newFen);
      setEditorError(check.error);
      return;
    }
    editorChess.load(newFen);
    setFen(newFen);
    setEditorError('');
  };

  const handleSquareClick = (square: string) => {
    if (eraserActive) {
      editorChess.remove(square as any);
    } else if (selectedPiece) {
      editorChess.put(
        {type: selectedPiece.type as any, color: selectedPiece.color as any},
        square as any,
      );
    }
    updateFenState();
  };

  const handlePieceDrop = (
    sourceSquare: string,
    targetSquare: string,
    piece: string,
  ) => {
    editorChess.remove(sourceSquare as any);
    if (targetSquare) {
      const color = piece[0] === 'w' ? 'w' : 'b';
      const type = piece[1].toLowerCase();
      editorChess.put({type: type as any, color: color as any}, targetSquare as any);
    }
    updateFenState();
    return true;
  };

  const handleClearBoard = () => {
    editorChess.clear();
    updateFenState();
  };

  const handleResetBoard = () => {
    editorChess.load(START_FEN);
    setTurn('w');
    setWk(true);
    setWq(true);
    setBk(true);
    setBq(true);
    updateFenState();
  };

  useEffect(() => {
    updateFenState();
  }, [turn, wk, wq, bk, bq]);

  const whitePieces = [
    {type: 'k', color: 'w', label: '♔'},
    {type: 'q', color: 'w', label: '♕'},
    {type: 'r', color: 'w', label: '♖'},
    {type: 'b', color: 'w', label: '♗'},
    {type: 'n', color: 'w', label: '♘'},
    {type: 'p', color: 'w', label: '♙'},
  ];

  const blackPieces = [
    {type: 'k', color: 'b', label: '♚'},
    {type: 'q', color: 'b', label: '♛'},
    {type: 'r', color: 'b', label: '♜'},
    {type: 'b', color: 'b', label: '♝'},
    {type: 'n', color: 'b', label: '♞'},
    {type: 'p', color: 'b', label: '♟'},
  ];

  const renderPalette = (pieces: {type: string; color: string; label: string}[]) => (
    <View style={{flexDirection: 'row', gap: 6}}>
      {pieces.map((p, idx) => {
        const isSelected =
          selectedPiece?.type === p.type &&
          selectedPiece?.color === p.color &&
          !eraserActive;
        return (
          // @ts-ignore
          <div
            key={idx}
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              borderWidth: 1.5,
              borderStyle: 'solid',
              borderColor: isSelected ? '#B58E58' : '#E6E6E6',
              backgroundColor: isSelected ? '#FFFDF8' : '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={() => {
              setSelectedPiece(p);
              setEraserActive(false);
            }}>
            <img
              src={`https://lichess1.org/assets/piece/cburnett/${p.color}${p.type.toUpperCase()}.svg`}
              alt={p.label}
              className="draggable-palette-piece"
              draggable={true}
              onDragStart={(e: any) => {
                e.dataTransfer.setData('text/plain', JSON.stringify(p));
              }}
              style={{
                width: '68%',
                height: '68%',
              }}
            />
          </div>
        );
      })}
    </View>
  );

  return (
    <View style={{width: '100%'}}>
      {/* CSS drag overrides */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .draggable-palette-piece {
          -webkit-user-drag: element !important;
          user-drag: element !important;
          cursor: grab !important;
          user-select: none !important;
          -webkit-user-select: none !important;
        }
        .draggable-palette-piece:active {
          cursor: grabbing !important;
        }
      `,
        }}
      />

      <View style={{flexDirection: 'row', width: '100%'}}>
        {/* Left Column: Board + standard/clear */}
        <View style={{padding: 24, alignItems: 'center', gap: 12, borderRightWidth: 1, borderColor: '#E6E6E6'}}>
          {/* @ts-ignore */}
          <div
            style={{
              width: 250,
              height: 250,
              borderRadius: 8,
              overflow: 'hidden',
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'rgba(77,63,55,0.12)',
            }}
            onDragOverCapture={(e: any) => {
              e.preventDefault();
            }}
            onDropCapture={(e: any) => {
              try {
                const rawData = e.dataTransfer.getData('text/plain');
                if (!rawData) return;
                e.preventDefault();
                e.stopPropagation();
                const p = JSON.parse(rawData);

                if (p && p.type && p.color) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  const size = rect.width;
                  const squareSize = size / 8;
                  const col = Math.floor(x / squareSize);
                  const row = Math.floor(y / squareSize);

                  if (col >= 0 && col < 8 && row >= 0 && row < 8) {
                    const file = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'][col];
                    const rank = 8 - row;
                    const square = `${file}${rank}`;

                    editorChess.put(
                      {type: p.type as any, color: p.color as any},
                      square as any,
                    );
                    updateFenState();
                  }
                }
              } catch (err) {
                console.error('Failed to drop piece:', err);
              }
            }}>
            <Chessboard
              position={fen}
              onSquareClick={handleSquareClick}
              onPieceDrop={handlePieceDrop}
              boardWidth={250}
              customLightSquareStyle={{backgroundColor: squareColors.light}}
              customDarkSquareStyle={{backgroundColor: squareColors.dark}}
              dropOffBoardAction="trash"
            />
          </div>
          
          <View style={{flexDirection: 'row', gap: 8, justifyContent: 'center', width: '100%'}}>
            <TouchableOpacity
              onPress={handleResetBoard}
              activeOpacity={0.8}
              style={{
                borderWidth: 1,
                borderColor: '#E6E6E6',
                backgroundColor: '#FFFDF8',
                borderRadius: 100,
                paddingHorizontal: 16,
                paddingVertical: 5,
              }}>
              <Text style={{fontSize: fs(11), fontWeight: 'bold', color: '#7A6652'}}>Standard</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              onPress={handleClearBoard}
              activeOpacity={0.8}
              style={{
                borderWidth: 1,
                borderColor: '#E6E6E6',
                backgroundColor: '#FFFDF8',
                borderRadius: 100,
                paddingHorizontal: 16,
                paddingVertical: 5,
              }}>
              <Text style={{fontSize: fs(11), fontWeight: 'bold', color: '#7A6652'}}>Clear</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Right Column: Controls */}
        <View style={{flex: 1, padding: 24, gap: 14}}>
          {/* Pieces grid */}
          <View style={{gap: 4}}>
            <Text style={{fontSize: fs(10), fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>PIECES</Text>
            {renderPalette(whitePieces)}
            {renderPalette(blackPieces)}
          </View>

          {/* Eraser */}
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => {
              setSelectedPiece(null);
              setEraserActive(true);
            }}
            style={{
              height: 34,
              borderRadius: 6,
              borderWidth: 1,
              borderColor: eraserActive ? '#B58E58' : '#E6E6E6',
              backgroundColor: eraserActive ? '#FFFDF8' : '#FFFFFF',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}>
            <MaterialCommunityIcons name="eraser" size={14} color={eraserActive ? '#B58E58' : '#757575'} />
            <Text style={{fontSize: fs(11), fontWeight: 'bold', color: eraserActive ? '#B58E58' : '#757575'}}>Eraser</Text>
          </TouchableOpacity>

          {/* Turn */}
          <View style={{gap: 6}}>
            <Text style={{fontSize: fs(10), fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>TURN TO MOVE</Text>
            <View style={{flexDirection: 'row', backgroundColor: '#F5F5F5', borderRadius: 8, padding: 3, height: 36}}>
              <TouchableOpacity
                onPress={() => setTurn('w')}
                style={{
                  flex: 1,
                  borderRadius: 6,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: turn === 'w' ? '#FFFFFF' : 'transparent',
                  shadowColor: turn === 'w' ? '#000' : 'transparent',
                  shadowOffset: {width: 0, height: 1},
                  shadowOpacity: 0.15,
                  shadowRadius: 2,
                  elevation: 1,
                }}>
                <Text style={{fontSize: fs(12), fontWeight: turn === 'w' ? 'bold' : 'normal', color: turn === 'w' ? '#1E110A' : '#757575'}}>White</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setTurn('b')}
                style={{
                  flex: 1,
                  borderRadius: 6,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: turn === 'b' ? '#FFFFFF' : 'transparent',
                  shadowColor: turn === 'b' ? '#000' : 'transparent',
                  shadowOffset: {width: 0, height: 1},
                  shadowOpacity: 0.15,
                  shadowRadius: 2,
                  elevation: 1,
                }}>
                <Text style={{fontSize: fs(12), fontWeight: turn === 'b' ? 'bold' : 'normal', color: turn === 'b' ? '#1E110A' : '#757575'}}>Black</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Castling rights */}
          <View style={{gap: 6}}>
            <Text style={{fontSize: fs(10), fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>CASTLING RIGHTS</Text>
            <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 6}}>
              {[
                {label: 'W O-O', val: wk, setVal: setWk},
                {label: 'W O-O-O', val: wq, setVal: setWq},
                {label: 'B O-O', val: bk, setVal: setBk},
                {label: 'B O-O-O', val: bq, setVal: setBq},
              ].map((c, i) => (
                <TouchableOpacity
                  key={i}
                  activeOpacity={0.8}
                  onPress={() => c.setVal(!c.val)}
                  style={{
                    paddingHorizontal: 10,
                    paddingVertical: 6,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: c.val ? '#B58E58' : '#E6E6E6',
                    backgroundColor: c.val ? '#FFFDF8' : '#FFFFFF',
                  }}>
                  <Text
                    style={{
                      fontSize: fs(10),
                      fontWeight: 'bold',
                      color: c.val ? '#B58E58' : '#757575',
                    }}>
                    {c.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Resulting FEN */}
          <View style={{gap: 6}}>
            <Text style={{fontSize: fs(10), fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>RESULTING FEN</Text>
            <TextInput
              value={fen}
              onChangeText={text => {
                setFen(text);
                const check = validateFen(text);
                if (!check.ok) {
                  setEditorError(check.error);
                } else {
                  setEditorError('');
                  try {
                    editorChess.load(text);
                    const parts = text.split(' ');
                    if (parts.length > 1) setTurn(parts[1] as any);
                    if (parts.length > 2) {
                      const cRights = parts[2];
                      setWk(cRights.includes('K'));
                      setWq(cRights.includes('Q'));
                      setBk(cRights.includes('k'));
                      setBq(cRights.includes('q'));
                    }
                  } catch (e) {}
                }
              }}
              style={{
                height: 38,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: editorError ? '#FF4D4D' : '#E6E6E6',
                backgroundColor: '#F9F9F9',
                paddingHorizontal: 10,
                fontSize: fs(10.5),
                color: '#555555',
              }}
            />
            {!!editorError && (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  paddingVertical: 6,
                  paddingHorizontal: 10,
                  borderRadius: 8,
                  backgroundColor: '#FFF1F0',
                  borderWidth: 1,
                  borderColor: '#FFCCC7',
                }}>
                <Ionicons name="alert-circle" size={13} color="#FF4D4D" />
                <Text
                  style={{
                    fontSize: fs(10.5),
                    color: '#D93025',
                    fontWeight: '600',
                    flex: 1,
                  }}>
                  {editorError}
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>

      {/* Footer */}
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'flex-end',
          gap: 10,
          paddingHorizontal: 24,
          paddingBottom: 20,
          paddingTop: 10,
          borderTopWidth: 1,
          borderColor: '#F5F5F5',
        }}>
        <TouchableOpacity
          onPress={onClose}
          activeOpacity={0.8}
          style={{
            borderWidth: 1.5,
            borderColor: '#CCCCCC',
            backgroundColor: '#FFFFFF',
            borderRadius: 100,
            paddingHorizontal: 22,
            paddingVertical: 8,
          }}>
          <Text style={{fontSize: fs(12.5), fontWeight: 'bold', color: '#2E1B10'}}>Cancel</Text>
        </TouchableOpacity>

        <TouchableOpacity
          disabled={!!editorError}
          onPress={() => onApply(fen)}
          activeOpacity={0.8}
          style={{
            backgroundColor: '#FACF47',
            borderRadius: 100,
            paddingHorizontal: 24,
            paddingVertical: 8,
            opacity: editorError ? 0.5 : 1,
          }}>
          <Text style={{fontSize: fs(12.5), fontWeight: 'bold', color: '#1E110A'}}>Apply Position</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}
